import { ChevronsUpDown, Check, Search, Settings, Globe } from "lucide-react";
import { cn } from "@/infrastructure/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSuperAdminOrg } from "@/shared/contexts/SuperAdminOrgContext";
import { useIsSuperAdminRole } from "@/shared/hooks/use-system-role";
import { useIsAdmin } from "@/shared/hooks/use-admin-validation";
import { useWorkspace } from "@/features/Workspaces/state/WorkspaceContext";
import type { Organization } from "@/features/Organization/services/organizationService";

interface SuperAdminOrgSwitcherProps {
  orgName: string;
  orgLogo: string;
  logoUrl: string;
  planName?: string;
  subStatus?: string;
  statusLabel: Record<string, string>;
  statusColor: Record<string, string>;
  onLogoError: () => void;
  onNavigateSettings: () => void;
  /**
   * Barra lateral recolhida: só o avatar, com as configurações acima dele, e a
   * lista abrindo para o lado. Busca, plataforma e troca são as mesmas.
   */
  compact?: boolean;
}

/**
 * Troca de organização no card da sidebar. Era exclusiva do superadmin e passou a
 * servir qualquer usuário vinculado a mais de uma organização (AB#84509) — o
 * componente é o mesmo; o que muda é a lista, escopada pelo backend, e a opção de
 * "plataforma", que só existe para superadmin.
 */
export const SuperAdminOrgSwitcher = ({
  orgName,
  orgLogo,
  logoUrl,
  planName,
  subStatus,
  statusLabel,
  statusColor,
  onLogoError,
  onNavigateSettings,
  compact = false,
}: SuperAdminOrgSwitcherProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedOrganization, organizations, setSelectedOrganization, clearSelectedOrganization } =
    useSuperAdminOrg();
  const { isSuperAdmin } = useIsSuperAdminRole();
  // Admin da organização ATIVA, não do perfil: trocar para uma organização onde a
  // pessoa é membro comum tem que tirar a engrenagem do card junto (AB#84509).
  const { isAdmin } = useIsAdmin();
  const { setCurrentWorkspace } = useWorkspace();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  // Buscar entre duas organizações é ruído; entre trinta é necessário.
  const showSearch = organizations.length > 6;

  const filtered = organizations.filter((org: Organization) =>
    org.name.toLowerCase().includes(search.toLowerCase())
  );

  const hasSelectedOrg = !!selectedOrganization;

  // Em /admin/*, deixar o SuperAdminGuard renderizar o aviso em vez de redirecionar
  // automaticamente — preserva trabalho não salvo e dá ao usuário a chance de cancelar a troca.
  const isOnAdminArea = location.pathname.startsWith('/admin/');

  const handleSelectOrg = (org: Organization) => {
    setSelectedOrganization(org);
    setCurrentWorkspace(null);
    setOpen(false);
    setSearch("");
    if (!isOnAdminArea) {
      navigate("/");
    }
  };

  const handleSelectPlatform = () => {
    clearSelectedOrganization();
    setCurrentWorkspace(null);
    setOpen(false);
    setSearch("");
    if (!isOnAdminArea) {
      navigate("/");
    }
  };

  const settingsButton = hasSelectedOrg && isAdmin && (
    <button
      className="rounded-full h-7 w-7 flex items-center justify-center hover:bg-blue-100/50 dark:hover:bg-blue-900/50 transition-colors duration-200 flex-shrink-0"
      onClick={onNavigateSettings}
      title={t("navigation.settings")}
      aria-label={t("navigation.settings")}
    >
      <Settings className="h-4 w-4 text-muted-foreground" />
    </button>
  );

  return (
    <div className={cn("animate-fadeIn", compact ? "flex flex-col items-center gap-2" : "flex items-center gap-3")}>
      {compact && settingsButton}
      <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setSearch(""); }}>
        <PopoverTrigger asChild>
          <button
            title={compact ? (hasSelectedOrg ? orgName : t("navigation.selectOrganization", "Selecionar organização")) : undefined}
            className={cn(
              "flex items-center cursor-pointer hover:opacity-80 transition-opacity",
              compact ? "" : "gap-3 flex-1 min-w-0 text-left"
            )}
          >
            <div className={cn("relative rounded-lg overflow-hidden shadow-sm flex-shrink-0", compact ? "h-10 w-10" : "h-12 w-12")}>
              {hasSelectedOrg && orgLogo ? (
                <img
                  src={logoUrl}
                  alt={orgName}
                  className="object-contain w-full h-full bg-secondary p-1"
                  onError={onLogoError}
                />
              ) : hasSelectedOrg ? (
                <div className="w-full h-full flex items-center justify-center bg-primary text-primary-foreground">
                  {orgName?.[0]?.toUpperCase() || '?'}
                </div>
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-muted text-muted-foreground">
                  <Globe className="h-5 w-5" />
                </div>
              )}
            </div>
            {!compact && (
            <div className="flex-1 overflow-hidden">
              {hasSelectedOrg ? (
                <>
                  <p className="text-sm font-medium truncate mb-0.5">{orgName}</p>
                  {planName && (
                    <p className="text-xs truncate">
                      <span className="font-medium text-foreground underline underline-offset-2">{planName}</span>
                      {subStatus && planName?.toLowerCase() !== subStatus && (
                        <span className={cn("ml-1.5", statusColor[subStatus] || "text-muted-foreground")}>
                          · {statusLabel[subStatus] || subStatus}
                        </span>
                      )}
                    </p>
                  )}
                </>
              ) : (
                <>
                  <p className="text-sm font-medium truncate mb-0.5">{t("navigation.selectOrganization", "Selecionar organização")}</p>
                  {/* O rótulo de plataforma só faz sentido para superadmin; para os
                      demais, "sem organização selecionada" é estado transitório de carga. */}
                  {isSuperAdmin && (
                    <p className="text-xs text-muted-foreground truncate">{t("navigation.platformView", "SUPERADMIN")}</p>
                  )}
                </>
              )}
            </div>
            )}
            {!compact && <ChevronsUpDown className="h-3 w-3 text-muted-foreground flex-shrink-0" />}
          </button>
        </PopoverTrigger>
        <PopoverContent
          side={compact ? "right" : "top"}
          align={compact ? "end" : "start"}
          className="w-[260px] p-0"
          sideOffset={compact ? 12 : 8}
        >
          {showSearch && (
            <div className="p-2 border-b">
              <div className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-muted/50">
                <Search className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                <input
                  type="text"
                  placeholder={t("common.search", "Buscar...")}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                  autoFocus
                />
              </div>
            </div>
          )}
          <div className="max-h-[280px] overflow-y-auto">
            <div className="p-1">
              {/* Escopo de plataforma é papel de superadmin — não existe para os demais. */}
              {isSuperAdmin && (
                <>
                  <button
                    className={cn(
                      "flex items-center gap-2 w-full px-2 py-2 rounded-md text-left text-sm transition-colors",
                      "hover:bg-accent hover:text-accent-foreground",
                      !hasSelectedOrg && "bg-accent"
                    )}
                    onClick={handleSelectPlatform}
                  >
                    <div className="h-7 w-7 rounded overflow-hidden flex-shrink-0 flex items-center justify-center bg-muted text-muted-foreground">
                      <Globe className="h-4 w-4" />
                    </div>
                    <span className="truncate flex-1">{t("navigation.platformView", "SUPERADMIN")}</span>
                    {!hasSelectedOrg && (
                      <Check className="h-4 w-4 text-primary flex-shrink-0" />
                    )}
                  </button>
                  <div className="my-1 border-t border-border" />
                </>
              )}
              {filtered.map((org: Organization) => (
                <button
                  key={org.id}
                  className={cn(
                    "flex items-center gap-2 w-full px-2 py-2 rounded-md text-left text-sm transition-colors",
                    "hover:bg-accent hover:text-accent-foreground",
                    selectedOrganization?.id === org.id && "bg-accent"
                  )}
                  onClick={() => handleSelectOrg(org)}
                >
                  <div className="h-7 w-7 rounded overflow-hidden flex-shrink-0">
                    {org.logo_url ? (
                      <img src={org.logo_url} alt={org.name} className="object-contain w-full h-full bg-secondary p-0.5" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-primary/10 text-primary text-xs font-medium">
                        {org.name?.[0]?.toUpperCase() || '?'}
                      </div>
                    )}
                  </div>
                  <span className="truncate flex-1">{org.name}</span>
                  {selectedOrganization?.id === org.id && (
                    <Check className="h-4 w-4 text-primary flex-shrink-0" />
                  )}
                </button>
              ))}
              {filtered.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-3">
                  {t("common.noResults", "Nenhum resultado")}
                </p>
              )}
            </div>

          </div>
        </PopoverContent>
      </Popover>
      {!compact && settingsButton}
    </div>
  );
};
